import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CheckCircle,
  XCircle,
  Lock,
  Monitor,
  MapPin,
  Clock,
  ArrowLeft,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { SuperAdminLayout } from "@/components/SuperAdminLayout";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAdminAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SuperAdminLoginHistory() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    user_type: "",
    limit: 50,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const payload = {
        limit: filters.limit,
      };

      if (filters.status) payload.status = filters.status;
      if (filters.user_type) payload.user_type = filters.user_type;

      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/api/super-admin/login-history/stats`, {
          headers: getAdminAuthHeader(),
        }),
        axios.post(`${API}/api/super-admin/login-history/search`, payload, {
          headers: getAdminAuthHeader(),
        }),
      ]);

      setStats(statsRes.data);
      setLogs(logsRes.data.sample || []); // ✅ FIX
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load login history");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "failed":
        return <XCircle className="w-5 h-5 text-red-600" />;
      case "locked":
        return <Lock className="w-5 h-5 text-orange-600" />;
      default:
        return null;
    }
  };

  const getUserTypeColor = (userType) => {
    const colors = {
      super_admin: "bg-red-100 text-red-800",
      super_admin_employee: "bg-orange-100 text-orange-800",
      restaurant_admin: "bg-blue-100 text-blue-800",
      staff: "bg-purple-100 text-purple-800",
    };
    return colors[userType] || "bg-gray-100 text-gray-800";
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <SuperAdminLayout>
      <div>
        {/* Page Header */}
        <div
          className="mb-8 flex  w-full items-center px-6
  bg-gradient-to-r from-orange-500 to-red-500
  shadow-md text-white py-[1.65rem]"
        >
          <Button
            onClick={() => navigate("/admin/dashboard")}
            variant="ghost"
            className="hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Login History</h1>
            <p className=" mt-1">
              Monitor system access and security across all users
            </p>
          </div>
        </div>
        <div className="p-8">
          {/* Stats */}
          {stats && (
            <div className="mb-6">
              <div className="grid grid-cols-5 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">
                      Total Logins
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {stats.total_logins}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">
                      Successful
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {stats.successful_logins}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">
                      Failed
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">
                      {stats.failed_logins}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">
                      Unique Users
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {stats.unique_users}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-600">
                      Unique IPs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">
                      {stats.unique_ips}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Login Logs */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Login Attempts</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className="border rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          {getStatusIcon(log.status)}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">
                                {log.email || "Unknown User"}
                              </span>
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${getUserTypeColor(
                                  log.user_type
                                )}`}
                              >
                                {log.user_type.replace("_", " ")}
                              </span>
                              {log.status === "failed" &&
                                log.failure_reason && (
                                  <span className="text-xs text-red-600">
                                    ({log.failure_reason})
                                  </span>
                                )}
                            </div>

                            <div className="grid grid-cols-4 gap-4 text-sm text-gray-600 mt-2">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span>{log.ip_address}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Monitor className="w-4 h-4" />
                                <span>{log.browser || "Unknown"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Monitor className="w-4 h-4" />
                                <span>{log.os || "Unknown"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                <span>{formatDateTime(log.login_time)}</span>
                              </div>
                            </div>

                            {log.session_duration && (
                              <div className="mt-2 text-xs text-gray-500">
                                Session duration: {log.session_duration} minutes
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </SuperAdminLayout>
  );
}
